import {
  ApplicationRef,
  ComponentFactoryResolver,
  EmbeddedViewRef,
  EventEmitter,
  Injectable,
  Injector,
  Type
} from '@angular/core';
import { Subject } from 'rxjs';
import { IModalSettings } from '../modal.component';

export interface ModalRef<T = any> {
  instance: T;
  close: (result?: boolean) => void;
  afterClosed: () => Promise<boolean>;
  afterConfirmed: () => Promise<void>;
}

export interface IModalComponent {
  open?: () => void;
  close?: () => void;
  closed: EventEmitter<boolean>;
  confirmed?: EventEmitter<void>;
  settings?: IModalSettings;
}

@Injectable({
  providedIn: 'root'
})
export class ModalService {
  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private injector: Injector,
    private appRef: ApplicationRef,
  ) {}

  open<T extends IModalComponent>(component: Type<T>, config?: IModalSettings): ModalRef<T> {
    const factory = this.componentFactoryResolver.resolveComponentFactory(component);
    const componentRef = factory.create(this.injector);

    if (config) {
      componentRef.instance.settings = config;
    }
    this.appRef.attachView(componentRef.hostView);
    const domElem = (componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;
    document.body.appendChild(domElem);

    const closeSubject = new Subject<boolean>();
    const confirmSubject = new Subject<void>();

    const close = (result?: boolean) => {
      this.appRef.detachView(componentRef.hostView);
      componentRef.destroy();
      closeSubject.next(result);
      closeSubject.complete();
      confirmSubject.complete();
    };

    componentRef.instance.closed.subscribe((result: boolean) => close(result));

    if (componentRef.instance.confirmed) {
      componentRef.instance.confirmed.subscribe(() => {
        confirmSubject.next();
      });
    }

    if (componentRef.instance.open) {
      setTimeout(() => componentRef.instance.open!(), 0);
    }

    return {
      instance: componentRef.instance,
      close,
      afterClosed: () => closeSubject.toPromise(),
      afterConfirmed: () => confirmSubject.toPromise(),
    };
  }

}
